<template>
  <SelectButton
    v-model="value"
    class="event-chip-row"
    :options="events"
    option-label="label"
    option-value="id"
    data-key="id"
    :allow-empty="false"
    unstyled
    :pt="PASS_THROUGH"
    :aria-label="text.label"
  >
    <template #option="{ option }">
      <!-- LiveBadge's plain pip: the word is sr-only because the chip label
           beside it does not say "live". -->
      <LiveBadge v-if="option.live" variant="plain" size="sm" :sr-label="text.live" />
      <!-- On one line on purpose: the spaces either side of the middot are the
           design's separator, and a line break here would drop them. -->
      <span class="event-chip-row__label">{{ option.label }}<template v-if="hasCount(option)"> · <span class="event-chip-row__count">{{ option.count }}</span></template></span>
    </template>
  </SelectButton>
</template>

<script>
// PrimeVue's SelectButton, unstyled, is the row: one choice among several held
// in a model, each option a toggle button with `aria-pressed`, and the chosen one
// read-only so a second press cannot clear it. That is the whole of the
// behavior, and it is the widget's rather than a second copy of it here.
//
// Unstyled, because the widget's own look is the opposite of the design's: a
// joined segmented control, its segments sharing borders, each one clipping its
// overflow, and the chosen one drawn as a shadowed pill inset in its segment.
// The design draws separate 999px pills 8px apart in a strip that scrolls.
// Styled, every one of those rules would be restated here at a raised
// specificity to beat a stylesheet PrimeVue injects after this one; unstyled,
// the widget draws nothing, and these classes, passed through to the parts it
// renders, are the only paint there is.
//
// A plain `<script>` block rather than `<script setup>`: both of these are the
// same for every row, so they are built once for the module, and the validator
// has to live at module scope for `defineProps` to reach it.
const PASS_THROUGH = Object.freeze({
  pcToggleButton: {
    root: ({ context }) => ({
      class: ['event-chip-row__chip', { 'event-chip-row__chip--selected': context.active }],
    }),
    content: { class: 'event-chip-row__chip-content' },
  },
});

// Every event is `{ id, label, count?, live? }`: an id that no other event in
// the row shares, since it is both the model's value and each chip's key; a
// label to print; a count that is a whole number when it is there at all; and a
// `live` that is true or false when it is there at all.
function isValidEvents(events) {
  const ids = new Set();

  return events.every((event) => {
    if (typeof event?.id !== 'string' || event.id.length === 0 || ids.has(event.id)) {
      return false;
    }
    ids.add(event.id);

    return typeof event.label === 'string' && event.label.length > 0
      && (event.count === undefined || event.count === null || (Number.isInteger(event.count) && event.count >= 0))
      && (event.live === undefined || typeof event.live === 'boolean');
  });
}
</script>

<script setup>
import { LiveBadge } from '@hds/auto-auction-com-components-shared';
import { computed } from 'vue';
import SelectButton from 'primevue/selectbutton';

import { isValidEventChipRowI18n, mergeTranslations } from '../../utils/eventChipRowTranslations';

// The row of chips across the top of the Sale Viewer that chooses which of the
// sale's events the run list below it shows: every asset in the sale, one lane,
// or its timed assets. Each chip is the event's label with how many assets it
// holds, and a red dot before the label while that event is live.
//
// Presentation only. It holds the chosen event's id in its model and nothing
// else; which events a sale has, their counts and which are live are the
// page's, and so is filtering the run list by the id this reports.
defineOptions({ name: 'EventChipRow' });

const props = defineProps({
  // [{ id, label, count?, live? }], in the order the chips are drawn. `id` is
  // what the model holds while that chip is chosen, and is unique in the row.
  // `label` is the page's words, already in the reader's language ("All",
  // "Lane 1", "Timed"). `count` is how many assets the event holds, printed after
  // the label; absent or null prints no count, and zero prints "0". `live` draws
  // the red dot before the label while the event is running.
  events: { type: Array, required: true, validator: isValidEvents },
  // `{ translations }`, partial and merged over the English defaults; see
  // ../../utils/eventChipRowTranslations.js.
  i18n: { type: Object, default: null, validator: isValidEventChipRowI18n },
});

// The chosen event's id, or null while none is. An id the row does not hold
// presses no chip.
const value = defineModel({ type: String, default: null });

// Merged at read time, so a caller passing half the words gets its own and the
// English defaults for the rest.
const text = computed(() => mergeTranslations(props.i18n?.translations));

// The validator admits a count that is absent, null or a whole number, so a
// whole number is the one that prints.
const hasCount = (event) => Number.isInteger(event.count);
</script>

<style scoped>
/* The strip: the chips in one line, 8px apart, 20px in from either edge, and
   scrolling sideways when they run past the screen. The scrollbar is hidden,
   as FilterRow's chip strip hides its, since the design draws none.

   The 6px above and below the chips is not the design's: it is the room each
   chip's 44px hit area needs. A scrolling box clips what overflows it, and a
   clipped hit area is not a hit area, so the strip is 44 tall and the chips it
   draws are 32. */
.event-chip-row {
  display: flex;
  gap: var(--p-event-chip-row-gap);
  padding: var(--p-event-chip-row-padding);
  overflow-x: auto;
  scrollbar-width: none;
}

.event-chip-row::-webkit-scrollbar {
  display: none;
}

/* Each chip is a toggle button the SelectButton renders, so it carries none of
   this component's scope and is reached through `:deep()`; the classes are the
   ones PASS_THROUGH gives it. COMPONENTS.md's pill: 32 tall, `0 12px`, the token
   map's 999px corner and the 0.5px outline, its words at 12.5/700 on one line.
   A button does not inherit the page's face on its own, so it is told to.

   The ground and the outline move on selection over DESIGN_SYSTEM §5 - Motion's
   150ms, and a press settles the chip a little, as the library's other buttons
   do. */
.event-chip-row :deep(.event-chip-row__chip) {
  position: relative;
  flex-shrink: 0;
  height: 32px;
  display: inline-flex;
  align-items: center;
  padding: var(--p-event-chip-row-chip-padding);
  border: var(--p-event-chip-row-chip-border-width) solid var(--p-event-chip-row-chip-border-color);
  border-radius: var(--p-event-chip-row-chip-border-radius);
  background: var(--p-event-chip-row-chip-background);
  color: var(--p-event-chip-row-chip-color);
  font-family: inherit;
  font-size: var(--p-event-chip-row-chip-font-size);
  font-weight: var(--p-event-chip-row-chip-font-weight);
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 150ms ease, border-color 150ms ease, transform 150ms ease;
}

/* The 44px the phone's hit-target floor asks for, around the 32px pill the
   design draws: 6px above and below it, and 4px into the gap either side, half
   of it, so neighbors meet in the middle of the gap. A pseudo-element is part
   of its button's hit area and takes no room in the strip, so nothing the
   design measures moves.

   Its box is measured from inside the pill's 0.5px outline, which a browser
   rounds up to a whole pixel where it cannot draw a half — at 1x, and in
   Chrome at every density — so 6px and 4px past the pill are 7 and 5 from
   inside it. Where the outline does draw thinner, as WebKit draws it on a
   2x or 3x screen, what is left over runs into the strip's padding, which
   clips the hit area at 44, and under the neighbor's own in the gap. */
.event-chip-row :deep(.event-chip-row__chip::before) {
  content: '';
  position: absolute;
  inset: -7px -5px;
}

.event-chip-row :deep(.event-chip-row__chip:not(.event-chip-row__chip--selected):hover) {
  background: var(--p-event-chip-row-chip-hover-background);
}

.event-chip-row :deep(.event-chip-row__chip:not(.event-chip-row__chip--selected):active) {
  transform: scale(0.98);
}

.event-chip-row :deep(.event-chip-row__chip:focus-visible) {
  outline: 2px solid var(--p-event-chip-row-chip-focus-outline-color);
  outline-offset: 2px;
}

/* The chosen chip: the brand accent with its own ink. It keeps its outline, in
   the accent, so choosing a chip moves the outline's color and never the chip's
   size. */
.event-chip-row :deep(.event-chip-row__chip--selected) {
  background: var(--p-event-chip-row-chip-selected-background);
  border-color: var(--p-event-chip-row-chip-selected-border-color);
  color: var(--p-event-chip-row-chip-selected-color);
}

/* What the button holds: the dot, when there is one, 6px before the label. */
.event-chip-row :deep(.event-chip-row__chip-content) {
  display: inline-flex;
  align-items: center;
  gap: var(--p-event-chip-row-chip-content-gap);
}


/* The count is a figure a buyer compares across the row, so it is mono with
   tabular numerals, at the chip's size and weight. */
.event-chip-row__count {
  font-family: var(--p-event-chip-row-count-font-family);
  font-variant-numeric: tabular-nums;
}

/* Announced but not drawn. Absolutely positioned so it is not a flex item of
   the chip beside the dot, where it would take the chip's gap and move the
   label for sighted readers to say something only a screen reader hears. */
</style>
